import React, { useEffect, useState, useCallback } from "react";
import { passwordError, PASSWORD_HINT } from "@/lib/passwordPolicy";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import HotelLayout from "@/components/HotelLayout";
import { Users, Plus, Trash2, LoaderCircle, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];
const ROLE_LABELS = {
    otel_owner: "Otel Sahibi",
    otel_manager: "Otel Yöneticisi",
    otel_reception: "Resepsiyon",
    otel_accounting: "Muhasebe",
};
const ROLE_COLORS = {
    otel_owner: "bg-purple-500", otel_manager: "bg-emerald-500",
    otel_reception: "bg-sky-500", otel_accounting: "bg-amber-500",
};

const fmtDate = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

export default function HotelStaffPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ email: "", full_name: "", password: "", role: "otel_reception" });
    const [saving, setSaving] = useState(false);

    const canManage = user && ["otel_owner", "admin", "super_admin"].includes(user.role);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/hotel/staff");
            setStaff(r.data.items || []);
        } catch (e) {
            if (e.response?.status === 403) {
                toast.error("Personel listesini görmek için Yönetici veya Sahibi yetkisi gerekir");
            }
        } finally { setLoading(false); }
    }, []);

    /* eslint-disable */
    useEffect(() => {
        if (authLoading) return;
        if (!user) return navigate("/h/login");
        if (!HOTEL_ROLES.includes(user.role)) return navigate("/admin");
        load();
    }, [user, authLoading, navigate, load]);
    /* eslint-enable */

    const submit = async (e) => {
        e.preventDefault();
        if (passwordError(form.password)) { toast.error(passwordError(form.password)); return; }
        setSaving(true);
        try {
            await api.post("/hotel/staff", form);
            toast.success("Personel eklendi");
            setShowCreate(false);
            setForm({ email: "", full_name: "", password: "", role: "otel_reception" });
            load();
        } catch (e2) {
            toast.error(e2.response?.data?.detail || "Eklenemedi");
        } finally { setSaving(false); }
    };

    const del = async (id, name) => {
        if (!window.confirm(`"${name}" personeli silinsin mi?`)) return;
        try {
            await api.delete(`/hotel/staff/${id}`);
            toast.success("Silindi");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    const action = canManage ? (
        <button data-testid="h-staff-add" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm">
            <Plus className="h-3.5 w-3.5" /> Yeni Personel
        </button>
    ) : null;

    return (
        <HotelLayout title="Personel" hotelName={user?.full_name?.split(" ")[0]} action={action}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                {loading ? (
                    <div className="p-12 flex items-center justify-center"><LoaderCircle className="h-6 w-6 text-emerald-500 animate-spin" /></div>
                ) : staff.length === 0 ? (
                    <div className="p-12 text-center text-sm text-slate-500" data-testid="h-staff-empty">
                        <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        Henüz personel yok.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-slate-800/50">
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                                <th className="px-4 py-3">Ad Soyad</th>
                                <th className="px-4 py-3">E-posta</th>
                                <th className="px-4 py-3">Rol</th>
                                <th className="px-4 py-3">Eklenme</th>
                                <th className="px-4 py-3 text-right">Aksiyon</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {staff.map((s) => (
                                <tr key={s.id} data-testid={`h-staff-${s.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`h-8 w-8 rounded-full ${ROLE_COLORS[s.role] || "bg-slate-400"} text-white flex items-center justify-center font-bold text-xs flex-shrink-0`}>{(s.full_name || "?")[0]}</div>
                                            <div className="font-semibold text-slate-900 dark:text-white">{s.full_name}</div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{s.email}</td>
                                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${ROLE_COLORS[s.role] || "bg-slate-400"}`}>{ROLE_LABELS[s.role] || s.role}</span></td>
                                    <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(s.created_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        {canManage && s.id !== user?.id && (
                                            <button data-testid={`h-staff-del-${s.id}`} onClick={() => del(s.id, s.full_name)} className="p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/30 text-rose-600 dark:text-rose-400 transition-colors" title="Sil">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowCreate(false)}>
                    <form onSubmit={submit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="h-staff-create-form">
                        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <h2 className="font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-500" /> Yeni Personel</h2>
                            <button type="button" onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Ad Soyad</label>
                                <input data-testid="h-staff-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">E-posta</label>
                                <input data-testid="h-staff-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Şifre ({PASSWORD_HINT})</label>
                                <input data-testid="h-staff-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Rol</label>
                                <select data-testid="h-staff-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40">
                                    {Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="p-5 border-t border-slate-100 dark:border-slate-800 flex gap-2">
                            <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-bold">İptal</button>
                            <button data-testid="h-staff-submit" type="submit" disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold">{saving ? "Kaydediliyor..." : "Ekle"}</button>
                        </div>
                    </form>
                </div>
            )}
        </HotelLayout>
    );
}
